import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { PopoverSurface } from "@ui/components/PopoverSurface";
import { SoftButton } from "@ui/components/SoftButton";
import { formatDate } from "@ui/lib/format-date";
import { Link } from "@ui/primitives/Link";
import { DialogTrigger } from "@ui/primitives/Popover";
import type { Costs } from "../../apply-household";

export interface ProvenancePopoverProps {
  attributions: Costs["attributions"];
  total: Costs["total"];
  methodVersion: number;
  /** True when `as_of` is the instant we computed the figure rather than one a
   *  source observed — the two are different claims and the copy says which. */
  computed: boolean;
}

/** Where a figure came from, in the reader's own words rather than the panel's. */
export function ProvenancePopover({ attributions, computed, methodVersion, total }: ProvenancePopoverProps) {
  const confidence = total.confidence;
  const coverage = total.coverage;

  return (
    <DialogTrigger>
      <SoftButton
        aria-label={t("components.views.destinations.costs.provenance.trigger_label")}
        className="size-5 text-neutral-500 outline-none transition-colors hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
      >
        <Icon name="info" size="12" />
      </SoftButton>
      <PopoverSurface className="max-w-[24rem] p-5" placement="bottom start">
        <div className="flex flex-col gap-3">
          <h3 className="font-semibold text-base text-neutral-900">
            {t("components.views.destinations.costs.provenance.title")}
          </h3>
          {total.as_of && (
            <p className="text-neutral-600 text-sm">
              {computed
                ? t("components.views.destinations.costs.computed_at", { date: formatDate(total.as_of) })
                : t("components.views.destinations.costs.as_of", { date: formatDate(total.as_of) })}
            </p>
          )}
          {confidence !== null && (
            <p className="text-neutral-600 text-sm">
              {t("components.views.destinations.costs.confidence", { percent: Math.round(confidence * 100) })}
            </p>
          )}
          {coverage !== null && (
            <p className="text-neutral-600 text-sm">
              {t("components.views.destinations.costs.coverage", { percent: Math.round(coverage * 100) })}
            </p>
          )}
          {attributions.length > 0 && (
            <div className="flex flex-col gap-1 border-neutral-100 border-t pt-3">
              <h4 className="font-medium text-neutral-500 text-xs uppercase tracking-wider">
                {t("components.views.destinations.costs.sources")}
              </h4>
              {attributions.map((attribution) => (
                <p className="text-neutral-700 text-sm" key={attribution.source}>
                  {attribution.url ? (
                    <Link
                      className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-500"
                      href={attribution.url}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {attribution.text}
                    </Link>
                  ) : (
                    attribution.text
                  )}
                </p>
              ))}
            </div>
          )}
          <p className="text-neutral-400 text-xs">
            {t("components.views.destinations.costs.method", { version: methodVersion })}
          </p>
        </div>
      </PopoverSurface>
    </DialogTrigger>
  );
}
